import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, CheckCircle2, Plus, X, PartyPopper } from "lucide-react";
import PublicHeader from "@/components/PublicHeader";
import { api, formatApiError } from "@/lib/api";
import { track, captureUtm, getUtm } from "@/lib/analytics";
import {
  FITTER_SERVICES, FITTER_SERVICE_CHOICES, INSTALL_SERVICES, RADIUS_OPTIONS, AVAILABILITY_OPTIONS, PARTNER_TYPES,
} from "@/lib/fitters";

const DRAFT_KEY = "fm_fitter_draft";
const STEP_TITLES = ["About you", "Working area", "Services", "Availability"];

const EMPTY = {
  full_name: "", trading_name: "", mobile: "", email: "", postcode: "",
  working_radius_miles: null, areas_covered: [],
  partner_type: "fitter", services: [], years_experience: "", own_vehicle: false, can_collect: false,
  vehicle_type: "", max_roll_length_m: "", multiple_collections: false,
  availability: [], has_public_liability: false, agree_terms: false,
  public_liability_insurer: "", policy_expiry: "", website: "", description: "",
};

export default function FitterRegister() {
  const nav = useNavigate();
  const [params] = useSearchParams();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(DRAFT_KEY) || "null");
      return saved ? { ...EMPTY, ...saved } : EMPTY;
    } catch (e) {
      return EMPTY;
    }
  });
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    captureUtm();
    track("fitter_join_started", { from: params.get("from") || "direct" });
  }, []); // eslint-disable-line

  // Persist draft between steps so accidental navigation doesn't lose input.
  useEffect(() => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(form));
    } catch (e) { /* ignore */ }
  }, [form]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const toggleIn = (key, value) =>
    setForm((f) => {
      const arr = f[key] || [];
      return { ...f, [key]: arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value] };
    });

  function validateStep(s) {
    if (s === 1) {
      if (form.full_name.trim().length < 2) return "Please enter your full name.";
      const digits = form.mobile.replace(/\D/g, "");
      if (digits.length < 10) return "Please enter a valid UK mobile number.";
      if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) return "Please enter a valid email address.";
      if (form.postcode.trim().length < 2) return "Please enter your postcode.";
    }
    if (s === 2) {
      if (!form.working_radius_miles) return "Please choose your working radius.";
    }
    if (s === 3) {
      const isFitter = form.partner_type === "fitter" || form.partner_type === "both";
      const isDelivery = form.partner_type === "delivery" || form.partner_type === "both";
      if (isFitter && !form.services.some((x) => INSTALL_SERVICES.includes(x)))
        return "Select at least one flooring installation service.";
      if (isDelivery && !(form.can_collect || form.services.includes("flooring_collection")))
        return "As a delivery partner, please confirm you can collect flooring.";
      if (form.years_experience === "" || Number(form.years_experience) < 0)
        return "Please enter your years of experience.";
    }
    if (s === 4) {
      if (form.availability.length === 0) return "Please choose your typical availability.";
      if (!form.agree_terms) return "Please agree to the fitter terms and privacy notice.";
    }
    return null;
  }

  function next() {
    const err = validateStep(step);
    if (err) { setError(err); return; }
    setError(null);
    track("fitter_step_completed", { step });
    if (step < 4) setStep(step + 1);
  }
  function back() {
    setError(null);
    if (step > 1) setStep(step - 1);
  }

  async function submit() {
    const err = validateStep(4);
    if (err) { setError(err); return; }
    setSubmitting(true);
    setError(null);
    const utm = getUtm();
    const payload = {
      full_name: form.full_name.trim(),
      trading_name: form.trading_name.trim() || null,
      mobile: form.mobile.trim(),
      email: form.email.trim(),
      postcode: form.postcode.trim().toUpperCase(),
      working_radius_miles: Number(form.working_radius_miles),
      areas_covered: form.areas_covered,
      partner_type: form.partner_type,
      services: form.services,
      years_experience: Number(form.years_experience),
      own_vehicle: form.own_vehicle,
      can_collect: form.can_collect,
      vehicle_type: form.vehicle_type.trim() || null,
      max_roll_length_m: form.max_roll_length_m === "" ? null : Number(form.max_roll_length_m),
      multiple_collections: form.multiple_collections,
      availability: form.availability,
      has_public_liability: form.has_public_liability,
      agree_terms: form.agree_terms,
      public_liability_insurer: form.public_liability_insurer.trim() || null,
      policy_expiry: form.policy_expiry.trim() || null,
      website: form.website.trim() || null,
      description: form.description.trim() || null,
      source: utm.source || params.get("from") || null,
      campaign: utm.campaign || null,
    };
    try {
      const r = await api.post("/fitters/apply", payload);
      track("fitter_application_submitted", { partner_type: form.partner_type });
      localStorage.removeItem(DRAFT_KEY);
      setDone(r.data);
      window.scrollTo(0, 0);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  }

  if (done) return <Completion done={done} nav={nav} />;

  return (
    <>
      <PublicHeader />
      <main className="min-h-screen bg-slate-50 pb-28" data-testid="fitter-register-page">
        <div className="max-w-xl mx-auto px-4 sm:px-6 pt-6">
          <h1 className="font-display font-bold text-2xl text-slate-900">Join as a founding fitter</h1>
          <p className="text-sm text-slate-500 mt-1">No joining fee. Takes a couple of minutes.</p>

          {/* Progress */}
          <div className="mt-5" data-testid="fitter-progress">
            <div className="flex items-center justify-between text-xs font-semibold text-slate-500 mb-2">
              <span>Step {step} of 4 · {STEP_TITLES[step - 1]}</span>
              <span>{Math.round((step / 4) * 100)}%</span>
            </div>
            <div className="h-2 rounded-full bg-slate-200 overflow-hidden">
              <div className="h-full bg-[#E05915] transition-[width] duration-300" style={{ width: `${(step / 4) * 100}%` }} />
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl p-5 sm:p-6 mt-5">
            {step === 1 && <Step1 form={form} set={set} />}
            {step === 2 && <Step2 form={form} set={set} toggleIn={toggleIn} />}
            {step === 3 && <Step3 form={form} set={set} toggleIn={toggleIn} />}
            {step === 4 && <Step4 form={form} set={set} toggleIn={toggleIn} />}

            {error && (
              <div className="mt-4 bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 text-sm" data-testid="fitter-error">
                {error}
              </div>
            )}

            <div className="mt-6 flex gap-2">
              {step > 1 && (
                <button onClick={back} className="h-12 px-5 rounded-full border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-sm text-slate-700 inline-flex items-center gap-2" data-testid="fitter-back-btn">
                  <ArrowLeft className="w-4 h-4" /> Back
                </button>
              )}
              {step < 4 ? (
                <button onClick={next} className="flex-1 h-12 rounded-full bg-[#0F3D2E] hover:bg-[#0c3325] text-white font-semibold inline-flex items-center justify-center gap-2 transition-colors" data-testid="fitter-next-btn">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button onClick={submit} disabled={submitting} className="flex-1 h-12 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold inline-flex items-center justify-center gap-2 disabled:opacity-70 transition-colors" data-testid="fitter-submit-btn">
                  {submitting ? "Submitting…" : "Submit application"}
                </button>
              )}
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-4 text-center">
            Your contact details stay private and are only used by the Floor-Match team to review
            your application. You can add insurance documents and photos later.
          </p>
        </div>
      </main>
    </>
  );
}

/* ---------- Steps ---------- */
function Field({ label, hint, required, children }) {
  return (
    <label className="block">
      <span className="fm-label">{label}{required && <span className="text-[#E05915]"> *</span>}</span>
      {children}
      {hint && <span className="block text-xs text-slate-400 mt-1">{hint}</span>}
    </label>
  );
}

function Step1({ form, set }) {
  return (
    <div className="space-y-4">
      <Field label="Full name" required>
        <input className="fm-input" value={form.full_name} onChange={(e) => set({ full_name: e.target.value })} data-testid="f-full-name" autoComplete="name" />
      </Field>
      <Field label="Trading name" hint="If you trade under a business name">
        <input className="fm-input" value={form.trading_name} onChange={(e) => set({ trading_name: e.target.value })} data-testid="f-trading-name" />
      </Field>
      <Field label="Mobile number" required>
        <input className="fm-input" type="tel" inputMode="tel" value={form.mobile} onChange={(e) => set({ mobile: e.target.value })} data-testid="f-mobile" autoComplete="tel" placeholder="07…" />
      </Field>
      <Field label="Email address" required>
        <input className="fm-input" type="email" inputMode="email" value={form.email} onChange={(e) => set({ email: e.target.value })} data-testid="f-email" autoComplete="email" />
      </Field>
      <Field label="Home or business postcode" required>
        <input className="fm-input" value={form.postcode} onChange={(e) => set({ postcode: e.target.value.toUpperCase() })} data-testid="f-postcode" autoComplete="postal-code" placeholder="M1 1AE" />
      </Field>
    </div>
  );
}

function Step2({ form, set }) {
  const [entry, setEntry] = useState("");
  function add() {
    const v = entry.trim();
    if (v && !(form.areas_covered || []).includes(v)) set({ areas_covered: [...form.areas_covered, v] });
    setEntry("");
  }
  return (
    <div className="space-y-5">
      <div>
        <span className="fm-label">Working radius<span className="text-[#E05915]"> *</span></span>
        <div className="grid grid-cols-3 gap-2 mt-1" data-testid="f-radius">
          {RADIUS_OPTIONS.map((r) => (
            <button key={r} type="button" onClick={() => set({ working_radius_miles: r })}
              className={`h-12 rounded-xl border font-semibold text-sm transition-colors ${form.working_radius_miles === r ? "border-[#E05915] bg-[#E05915]/10 text-[#E05915]" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`}
              data-testid={`f-radius-${r}`}>
              {r} mi
            </button>
          ))}
        </div>
      </div>
      <div>
        <span className="fm-label">Towns or postcode districts you cover</span>
        <div className="flex gap-2 mt-1">
          <input className="fm-input flex-1" value={entry} onChange={(e) => setEntry(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
            placeholder="e.g. Stockport, SK1, Salford" data-testid="f-area-input" />
          <button type="button" onClick={add} className="h-12 px-4 rounded-xl bg-[#0F3D2E] text-white font-semibold inline-flex items-center gap-1" data-testid="f-area-add">
            <Plus className="w-4 h-4" /> Add
          </button>
        </div>
        {form.areas_covered.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-3">
            {form.areas_covered.map((a) => (
              <span key={a} className="inline-flex items-center gap-1.5 bg-slate-100 text-slate-800 rounded-full pl-3 pr-2 py-1.5 text-sm">
                {a}
                <button type="button" onClick={() => set({ areas_covered: form.areas_covered.filter((x) => x !== a) })} aria-label={`Remove ${a}`}>
                  <X className="w-3.5 h-3.5 text-slate-500 hover:text-slate-800" />
                </button>
              </span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function Step3({ form, set, toggleIn }) {
  const showDelivery = form.partner_type === "delivery" || form.partner_type === "both";
  return (
    <div className="space-y-5">
      <div>
        <span className="fm-label">What do you offer?<span className="text-[#E05915]"> *</span></span>
        <div className="grid gap-2 mt-1" data-testid="f-partner-type">
          {PARTNER_TYPES.map((p) => (
            <button key={p.value} type="button" onClick={() => set({ partner_type: p.value })}
              className={`text-left p-3.5 rounded-xl border transition-colors ${form.partner_type === p.value ? "border-[#E05915] bg-[#E05915]/5" : "border-slate-300 bg-white hover:border-slate-400"}`}
              data-testid={`f-partner-${p.value}`}>
              <div className="font-semibold text-slate-900 text-sm">{p.label}</div>
              <div className="text-xs text-slate-500">{p.hint}</div>
            </button>
          ))}
        </div>
      </div>

      <div>
        <span className="fm-label">Services you provide</span>
        <div className="grid sm:grid-cols-2 gap-2 mt-1" data-testid="f-services">
          {FITTER_SERVICE_CHOICES.map((s) => {
            const on = form.services.includes(s.value);
            return (
              <button key={s.value} type="button" onClick={() => toggleIn("services", s.value)}
                className={`flex items-center gap-2 text-left p-3 rounded-xl border text-sm transition-colors ${on ? "border-[#2A5A3B] bg-[#2A5A3B]/5 text-slate-900" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`}
                data-testid={`f-service-${s.value}`}>
                <span className={`grid place-items-center w-5 h-5 rounded border ${on ? "bg-[#2A5A3B] border-[#2A5A3B]" : "border-slate-300"}`}>
                  {on && <CheckCircle2 className="w-4 h-4 text-white" />}
                </span>
                {s.label}
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Years of experience" required>
          <input className="fm-input" type="number" inputMode="numeric" min="0" value={form.years_experience} onChange={(e) => set({ years_experience: e.target.value })} data-testid="f-experience" />
        </Field>
        <div>
          <span className="fm-label">Own vehicle?</span>
          <YesNo value={form.own_vehicle} onChange={(v) => set({ own_vehicle: v })} testId="f-own-vehicle" />
        </div>
        <div>
          <span className="fm-label">Able to collect flooring?</span>
          <YesNo value={form.can_collect} onChange={(v) => set({ can_collect: v })} testId="f-can-collect" />
        </div>
      </div>

      {showDelivery && (
        <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 space-y-4" data-testid="f-delivery-block">
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">Collection & delivery details</p>
          <div className="grid sm:grid-cols-2 gap-4">
            <Field label="Vehicle type" hint="e.g. Transit van, Luton, estate">
              <input className="fm-input" value={form.vehicle_type} onChange={(e) => set({ vehicle_type: e.target.value })} data-testid="f-vehicle-type" />
            </Field>
            <Field label="Max roll length carried (m)">
              <input className="fm-input" type="number" inputMode="decimal" min="0" step="0.5" value={form.max_roll_length_m} onChange={(e) => set({ max_roll_length_m: e.target.value })} data-testid="f-max-roll" />
            </Field>
          </div>
          <div>
            <span className="fm-label">Can you make multiple retailer collections in one trip?</span>
            <YesNo value={form.multiple_collections} onChange={(v) => set({ multiple_collections: v })} testId="f-multi-collect" />
          </div>
        </div>
      )}
    </div>
  );
}

function Step4({ form, set, toggleIn }) {
  return (
    <div className="space-y-5">
      <div>
        <span className="fm-label">Typical availability<span className="text-[#E05915]"> *</span></span>
        <div className="grid grid-cols-2 gap-2 mt-1" data-testid="f-availability">
          {AVAILABILITY_OPTIONS.map((a) => {
            const on = form.availability.includes(a.value);
            return (
              <button key={a.value} type="button" onClick={() => toggleIn("availability", a.value)}
                className={`h-12 rounded-xl border font-semibold text-sm transition-colors ${on ? "border-[#2A5A3B] bg-[#2A5A3B]/10 text-[#2A5A3B]" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`}
                data-testid={`f-avail-${a.value}`}>
                {a.label}
              </button>
            );
          })}
        </div>
      </div>
      <div>
        <span className="fm-label">Public liability insurance?</span>
        <YesNo value={form.has_public_liability} onChange={(v) => set({ has_public_liability: v })} testId="f-pli" />
      </div>
      {form.has_public_liability && (
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Insurer" hint="Optional">
            <input className="fm-input" value={form.public_liability_insurer} onChange={(e) => set({ public_liability_insurer: e.target.value })} data-testid="f-insurer" />
          </Field>
          <Field label="Policy expiry" hint="Optional">
            <input className="fm-input" type="date" value={form.policy_expiry} onChange={(e) => set({ policy_expiry: e.target.value })} data-testid="f-policy-expiry" />
          </Field>
        </div>
      )}
      <Field label="Website or social profile" hint="Optional">
        <input className="fm-input" value={form.website} onChange={(e) => set({ website: e.target.value })} data-testid="f-website" placeholder="https://" />
      </Field>
      <Field label="Short description" hint="Optional — tell us a little about your work">
        <textarea className="fm-input min-h-[90px]" value={form.description} onChange={(e) => set({ description: e.target.value })} data-testid="f-description" maxLength={1000} />
      </Field>
      <label className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 bg-slate-50 cursor-pointer">
        <input type="checkbox" checked={form.agree_terms} onChange={(e) => set({ agree_terms: e.target.checked })} className="mt-1 w-5 h-5 accent-[#E05915]" data-testid="f-agree-terms" />
        <span className="text-sm text-slate-700">
          I agree to the FloorMatch fitter terms and privacy notice, and consent to being contacted
          about fitting opportunities.<span className="text-[#E05915]"> *</span>
        </span>
      </label>
      <p className="text-xs text-slate-400">
        Insurance proof, profile photo and examples of your work can be added later once your
        application is reviewed — no uploads needed now.
      </p>
    </div>
  );
}

function YesNo({ value, onChange, testId }) {
  return (
    <div className="grid grid-cols-2 gap-2 mt-1" data-testid={testId}>
      {[{ v: true, l: "Yes" }, { v: false, l: "No" }].map((o) => (
        <button key={o.l} type="button" onClick={() => onChange(o.v)}
          className={`h-11 rounded-xl border font-semibold text-sm transition-colors ${value === o.v ? "border-[#0F3D2E] bg-[#0F3D2E]/10 text-[#0F3D2E]" : "border-slate-300 bg-white text-slate-700 hover:border-slate-400"}`}
          data-testid={`${testId}-${o.l.toLowerCase()}`}>
          {o.l}
        </button>
      ))}
    </div>
  );
}

function Completion({ done, nav }) {
  return (
    <>
      <PublicHeader />
      <main className="min-h-screen bg-slate-50 grid place-items-center px-4 py-12" data-testid="fitter-complete-page">
        <div className="bg-white border border-slate-200 rounded-2xl p-8 max-w-md w-full text-center">
          <span className="inline-grid place-items-center w-16 h-16 rounded-full bg-[#2A5A3B]/10 text-[#2A5A3B] mx-auto">
            <PartyPopper className="w-8 h-8" aria-hidden="true" />
          </span>
          <h1 className="font-display font-bold text-2xl text-slate-900 mt-5">Application received</h1>
          <p className="text-slate-600 text-sm mt-3">
            Thank you for joining the FloorMatch founding fitter network. We'll review your details
            and contact you before sending any live fitting opportunities.
          </p>
          <div className="grid grid-cols-2 gap-3 mt-6">
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <div className="text-xs text-slate-500">Your postcode</div>
              <div className="font-display font-bold text-lg text-slate-900" data-testid="complete-postcode">{done.postcode}</div>
            </div>
            <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
              <div className="text-xs text-slate-500">Working radius</div>
              <div className="font-display font-bold text-lg text-slate-900" data-testid="complete-radius">{done.working_radius_miles} miles</div>
            </div>
          </div>
          <p className="text-xs text-slate-400 mt-3">Reference {done.reference_code}</p>
          <div className="flex flex-col gap-2 mt-6">
            <button onClick={() => nav("/fitter/dashboard")} className="h-12 rounded-full bg-[#E05915] hover:bg-[#c24b10] text-white font-semibold transition-colors" data-testid="complete-examples-btn">
              View example jobs
            </button>
            <Link to="/" className="h-12 grid place-items-center rounded-full border border-slate-300 bg-white hover:bg-slate-50 font-semibold text-sm text-slate-700" data-testid="complete-home-btn">
              Return to FloorMatch
            </Link>
          </div>
        </div>
      </main>
    </>
  );
}
